import React, { useState, useEffect, Suspense, lazy } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import usePipeline from '../hooks/usePipeline';
import useSSE from '../hooks/useSSE';
import PipelineCard from './PipelineCard.jsx';
import ClientStageTab from './ClientStageTab';
import DisqualifiedTab from './DisqualifiedTab';
import PlacedTab from './PlacedTab';
import InternalClientStageManager from './InternalClientStageManager';
import { API_BASE_URL } from '../config/api';

const API = API_BASE_URL;
const CandidatePreviewPanel = lazy(() => import('./CandidatePreviewPanel'));

const PipelineBoard = ({ pipelineId, activeView, hideManageStagesButton = false }) => {
  const { pipeline, loading, error, moveCard, reload } = usePipeline(pipelineId);

  // Real-time updates via SSE
  useSSE(pipelineId, {
    onCardMoved: reload,
    onRatingUpdated: reload,
    onStatusChanged: reload,
    onFeedbackAdded: reload,
    onMeetingUpdated: reload,
    onStageChanged: reload,
    onCandidateReactivated: reload,
  });
  const [previewCandidate, setPreviewCandidate] = useState(null);
  const [activeTab, setActiveTab] = useState(activeView || 'kanban');
  const [showStageManager, setShowStageManager] = useState(false);
  const [slaPopover, setSlaPopover] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Sync with external activeView prop
  useEffect(() => { if (activeView) setActiveTab(activeView); }, [activeView]);

  // Listen for the "open stage manager" event dispatched by
  // SearchDetailPage's tool-styled Manage Stages button in the sub-tab
  // bar. Mirrors the banyak:focus_tag_search pattern used elsewhere —
  // avoids threading a ref up to SearchDetailPage.
  useEffect(() => {
    const handler = () => setShowStageManager(true);
    window.addEventListener('banyak:open_stage_manager', handler);
    return () => window.removeEventListener('banyak:open_stage_manager', handler);
  }, []);

  // Auto-open candidate preview from URL params (e.g. ?card=58&panel=review)
  useEffect(() => {
    const cardParam = searchParams.get('card');
    if (cardParam && pipeline?.stages) {
      const allCards = pipeline.stages.flatMap(s => s.cards || []);
      const card = allCards.find(c => c.id === parseInt(cardParam));
      if (card) {
        const panel = searchParams.get('panel');
        setPreviewCandidate({ 
          candidate_id: card.candidate_id, 
          card_id: card.id, 
          openPresentation: panel === 'presentation',
          openFeedback: panel === 'review'
        });
        searchParams.delete('card');
        searchParams.delete('panel');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [pipeline]);

  const token = localStorage.getItem('authToken');
  const authHeaders = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const onDragEnd = async (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;
    const cardId = parseInt(draggableId.replace('card-', ''));
    const newStageId = parseInt(destination.droppableId.replace('stage-', ''));
    const destStage = pipeline.stages.find(s => s.id === newStageId);
    if (destStage && destStage.sends_to_client) {
      const card = pipeline.stages.flatMap(s => s.cards || []).find(c => c.id === cardId);
      if (!window.confirm(`Send to Client?\n\nMove "${card?.name}" to "${destStage.name}".\nThis will make them visible in the client portal and send an email.\n\nProceed?`)) return;
    }
    try { await moveCard(cardId, newStageId); }
    catch (err) { alert('Failed to move card: ' + err.message); }
  };

  const handleCardClick = (card) => {
    setPreviewCandidate({ candidate_id: card.candidate_id, card_id: card.id, openPresentation: true });
  };
  const handleDisqualifiedCardClick = (card) => {
    setPreviewCandidate({ candidate_id: card.candidate_id, card_id: card.id, openFeedback: true });
  };
  const handleClientCardClick = (candidate) => {
    setPreviewCandidate({ candidate_id: candidate.candidate_id, card_id: candidate.card_id });
  };

  // Save SLA settings for a stage
  const saveSla = async (stageId, greenHours, yellowHours) => {
    try {
      await axios.put(`${API}/api/stages/${stageId}`, { sla_green_hours: greenHours, sla_yellow_hours: yellowHours }, { headers: authHeaders });
      reload();
      setSlaPopover(null);
    } catch (err) { console.error('Error saving SLA:', err); }
  };

  if (loading) return <div className="flex-1 flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
  if (error) return <div className="flex-1 flex items-center justify-center text-red-600 text-sm">{error}</div>;
  if (!pipeline) return <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">Pipeline not found</div>;

  const activeStages = pipeline.stages?.filter(s => !['Disqualified', 'Placed', 'Rejected', 'Hired'].includes(s.name)) || [];
  const totalCards = activeStages.reduce((sum, s) => sum + (s.cards?.length || 0), 0);
  const dqStage = pipeline.stages?.find(s => s.name === 'Disqualified' || s.name === 'Rejected');
  const placedStage = pipeline.stages?.find(s => s.name === 'Placed' || s.name === 'Hired');

  const tabs = [
    { key: 'kanban', label: 'Internal Pipeline', count: totalCards, activeColor: 'border-blue-500 text-blue-600', badgeColor: 'bg-blue-100 text-blue-700' },
    { key: 'client-stages', label: 'Client Stages', count: null, activeColor: 'border-blue-500 text-blue-600', badgeColor: '' },
    { key: 'disqualified', label: 'Disqualified', count: dqStage?.cards?.length || 0, activeColor: 'border-red-500 text-red-600', badgeColor: 'bg-red-100 text-red-700' },
    { key: 'placed', label: 'Placed', count: placedStage?.cards?.length || 0, activeColor: 'border-green-500 text-green-600', badgeColor: 'bg-green-100 text-green-700' },
  ];

  return (
    <div className="pipeline-board flex-1 flex flex-col bg-gray-50 overflow-hidden">
      {/* Tabs — only show when not externally controlled */}
      {!activeView && (
      <div className="bg-white border-b px-4 flex-shrink-0">
        <div className="flex">
          {tabs.map(tab => {
            const isActive = activeTab === tab.key;
            return (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2.5 text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${
                  isActive ? tab.activeColor : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}>
                {tab.label}
                {tab.count != null && tab.count > 0 && (
                  <span className={`ml-1.5 px-1.5 py-0.5 text-[10px] rounded-full font-semibold ${
                    isActive ? tab.badgeColor : 'bg-gray-100 text-gray-500'
                  }`}>{tab.count}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>
      )}

      {/* ══════ INTERNAL PIPELINE (Kanban) ══════ */}
      {activeTab === 'kanban' && (
        <div className="flex-1 flex flex-col overflow-hidden">
          {!hideManageStagesButton && (
            <div className="flex items-center justify-end px-4 pt-2 pb-1 flex-shrink-0">
              <button onClick={() => setShowStageManager(true)}
                className="px-2.5 py-1 text-[11px] bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" /></svg>
                Manage Stages
              </button>
            </div>
          )}
          <DragDropContext onDragEnd={onDragEnd}>
            <div className={`flex-1 overflow-x-auto overflow-y-hidden px-4 pb-4 ${hideManageStagesButton ? 'pt-3' : ''}`}>
              <div className="flex gap-3 h-full min-w-0">
                {activeStages.map((stage) => {
                  const greenH = stage.sla_green_hours ?? 24;
                  const yellowH = stage.sla_yellow_hours ?? 48;
                  return (
                  <Droppable key={stage.id} droppableId={`stage-${stage.id}`}>
                    {(provided, snapshot) => (
                      <div ref={provided.innerRef} {...provided.droppableProps}
                        className={`flex-1 min-w-[240px] bg-white border rounded-lg flex flex-col overflow-hidden ${snapshot.isDraggingOver ? 'border-blue-400 bg-blue-50/50' : 'border-gray-200'}`}>
                        {/* Stage Header with SLA settings */}
                        <div className="px-3 py-2 border-b flex items-center bg-gray-50/80 gap-1.5 relative">
                          <span className="text-xs font-semibold text-gray-700 truncate flex-1">{stage.name}</span>
                          {/* SLA clock button */}
                          <button onClick={(e) => { e.stopPropagation(); setSlaPopover(slaPopover === stage.id ? null : stage.id); }}
                            className="w-5 h-5 rounded flex items-center justify-center text-gray-400 hover:text-blue-600 hover:bg-blue-50"
                            title="SLA settings">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </button>
                          <span className="text-[10px] text-gray-400 font-medium">{stage.cards?.length || 0}</span>

                          {/* SLA Popover */}
                          {slaPopover === stage.id && (
                            <>
                              <div className="fixed inset-0 z-40" onClick={() => setSlaPopover(null)} />
                              <SlaPopover greenHours={greenH} yellowHours={yellowH}
                                onSave={(g, y) => saveSla(stage.id, g, y)} onClose={() => setSlaPopover(null)} />
                            </>
                          )}
                        </div>
                        <div className="flex-1 overflow-y-auto p-1.5 space-y-1.5">
                          {stage.cards?.map((card, index) => (
                            <Draggable key={card.id} draggableId={`card-${card.id}`} index={index}>
                              {(provided, snapshot) => (
                                <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}
                                  onClick={() => handleCardClick(card)}>
                                  <PipelineCard card={card} isDragging={snapshot.isDragging}
                                    slaGreenHours={greenH} slaYellowHours={yellowH} onReload={reload} />
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                          {(!stage.cards || stage.cards.length === 0) && (
                            <div className="text-center py-8 text-[10px] text-gray-300">Drop here</div>
                          )}
                        </div>
                      </div>
                    )}
                  </Droppable>
                  );
                })}
              </div>
            </div>
          </DragDropContext>
          {showStageManager && (
            <InternalClientStageManager pipelineId={pipelineId} onClose={() => { setShowStageManager(false); reload(); }} isInternal={true} />
          )}
        </div>
      )}

      {activeTab === 'client-stages' && (
        <div className="flex-1 overflow-auto">
          <ClientStageTab pipelineId={pipelineId} onCardClick={handleClientCardClick} hideManageStagesButton={hideManageStagesButton} />
        </div>
      )}

      {activeTab === 'disqualified' && (
        <div className="flex-1 overflow-auto">
          <DisqualifiedTab pipelineId={pipelineId} onCardClick={handleDisqualifiedCardClick} onReload={reload} />
        </div>
      )}

      {activeTab === 'placed' && (
        <div className="flex-1 overflow-auto">
          <PlacedTab pipelineId={pipelineId} onCardClick={handleCardClick} onReload={reload} />
        </div>
      )}

      {previewCandidate && (
        <Suspense fallback={null}>
          <CandidatePreviewPanel
            candidateId={previewCandidate.candidate_id}
            cardId={previewCandidate.card_id}
            pipelineId={parseInt(pipelineId)}
            pipelineName={pipeline?.name}
            isOpen={!!previewCandidate}
            onClose={() => { setPreviewCandidate(null); reload(); }}
            autoOpenFeedback={activeTab === 'client-stages' || !!previewCandidate?.openFeedback}
            autoOpenPresentation={!!previewCandidate?.openPresentation}
            onCardDelete={() => { setPreviewCandidate(null); reload(); }}
          />
        </Suspense>
      )}
    </div>
  );
}

/**
 * SlaPopover — small dropdown to set green/yellow hour thresholds.
 */
function SlaPopover({ greenHours, yellowHours, onSave, onClose }) {
  const [green, setGreen] = useState(greenHours);
  const [yellow, setYellow] = useState(yellowHours);

  return (
    <div className="absolute right-0 top-full mt-1 w-52 bg-white border rounded-lg shadow-xl z-50 p-3" onClick={(e) => e.stopPropagation()}>
      <p className="text-[10px] font-semibold text-gray-600 mb-2 uppercase tracking-wide">SLA Thresholds</p>
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0"></span>
          <span className="text-[10px] text-gray-500 w-14">On track</span>
          <input type="number" value={green} onChange={(e) => setGreen(parseInt(e.target.value) || 0)}
            className="flex-1 px-2 py-1 text-[10px] border rounded focus:ring-1 focus:ring-blue-500 w-16" min="1" />
          <span className="text-[10px] text-gray-400">hrs</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-yellow-400 flex-shrink-0"></span>
          <span className="text-[10px] text-gray-500 w-14">Warning</span>
          <input type="number" value={yellow} onChange={(e) => setYellow(parseInt(e.target.value) || 0)}
            className="flex-1 px-2 py-1 text-[10px] border rounded focus:ring-1 focus:ring-blue-500 w-16" min="1" />
          <span className="text-[10px] text-gray-400">hrs</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0"></span>
          <span className="text-[10px] text-gray-500 w-14">Overdue</span>
          <span className="flex-1 text-[10px] text-gray-400 px-2">After {yellow}hrs</span>
        </div>
      </div>
      <div className="flex gap-1.5 mt-3 pt-2 border-t">
        <button onClick={onClose} className="flex-1 px-2 py-1 text-[10px] border rounded hover:bg-gray-50">Cancel</button>
        <button onClick={() => onSave(green, yellow)} className="flex-1 px-2 py-1 text-[10px] bg-blue-600 text-white rounded hover:bg-blue-700">Save</button>
      </div>
    </div>
  );
}

export default PipelineBoard;
